/**
 * The hospital doctor's side of the consultation.
 *
 * A doctor employed by a hospital works inside it (Dashboard 7 of the
 * upgrade): the patient in the room has a hospital file, the secretary has
 * usually run the AI over it already, and anything the doctor orders — labs,
 * scans, medication, a bed, an operation — is a service request that Contracts
 * approves, never the doctor. This panel is those four things for the patient
 * in the room, plus the operations waiting on the doctor's own yes (the
 * surgeon is surgery's second approver and the one who sets its time).
 */

import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, EmptyState, Field, Icon, Modal, Spinner, DateTimeInput } from '../../../components/common';
import { Tile3D } from '../../../components/common/Tile3D';
import { aiBatchApi, patientsApi, requestsApi } from '../../../api/endpoints';
import { useAuth } from '../../../context/AuthContext';
import { useNotifications } from '../../../context/NotificationContext';
import { usePoll } from '../../../hooks/usePolling';
import { MedicalFileView } from '../../hospitalShared/MedicalFileView';
import { RequestComposer } from '../../hospitalShared/RequestComposer';
import { RequestCard } from '../../hospitalShared/RequestCard';
import { PortalLinkPanel } from '../../hospitalShared/PortalLinkPanel';
import { NoteDialog } from '../../hospitalShared/NoteDialog';
import { formatDateTime } from '../../hospitalShared/labels';
import { LiveQueueBoard } from '../../queue/LiveQueueBoard';

const toLocalInput = (value) => {
  const d = value ? new Date(value) : new Date(Date.now() + 24 * 3600 * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** The surgeon's yes, with the time — or a no, or a change, with a reason. */
function SurgeryDecision({ request, onDone }) {
  const { success, error: toastError } = useNotifications();
  const [startAt, setStartAt] = useState(toLocalInput(request.scheduledAt));
  const [endAt, setEndAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [noting, setNoting] = useState(null);

  const decide = async (decision, note = null) => {
    setBusy(true);
    try {
      await requestsApi.surgery(request.id, {
        side: 'doctor',
        decision,
        note,
        scheduledAt: decision === 'accepted' ? new Date(startAt).toISOString() : undefined,
        expectedEndAt: decision === 'accepted' && endAt ? new Date(endAt).toISOString() : undefined,
      });
      success(decision === 'accepted' ? 'تمت موافقتك وتحديد موعد العملية' : 'تم إرسال قرارك');
      setNoting(null);
      onDone();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="w-full space-y-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Field label="موعد العملية">
          <DateTimeInput value={startAt} onChange={(e) => setStartAt(e.target.value)} />
        </Field>
        <Field label="الانتهاء المتوقع">
          <DateTimeInput value={endAt} onChange={(e) => setEndAt(e.target.value)} />
        </Field>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="success" icon="check" loading={busy} onClick={() => decide('accepted')}>
          موافقة وتحديد الموعد
        </Button>
        <Button variant="subtle" icon="edit-3" onClick={() => setNoting('modification_requested')}>
          طلب تعديل
        </Button>
        <Button variant="danger" icon="x-circle" onClick={() => setNoting('rejected')}>
          رفض
        </Button>
      </div>
      <NoteDialog
        open={Boolean(noting)}
        onClose={() => setNoting(null)}
        onSubmit={(note) => decide(noting, note)}
        title={noting === 'rejected' ? 'رفض العملية' : 'طلب تعديل على العملية'}
        destructive={noting === 'rejected'}
        confirmLabel="إرسال"
      />
    </div>
  );
}

/** Everything routed to or requested by this doctor that is still open. */
function MyRequests({ refreshKey }) {
  const { doctorId } = useAuth();
  const [rows, setRows] = useState(null);
  const load = useCallback(() => requestsApi.list({ queue: 'doctor', open: '1' }).then(setRows).catch(() => setRows([])), []);
  useEffect(() => {
    load();
  }, [load, refreshKey]);
  usePoll(load, 30000, { immediate: false });

  if (!rows) return <Spinner label="جارٍ تحميل الطلبات" variant="inline" />;
  if (rows.length === 0) return <p className="text-sm text-slate-500">لا توجد طلبات مفتوحة.</p>;

  // Only the operations this doctor is the surgeon of — one they requested for
  // a colleague is the colleague's to decide, and the server would refuse it.
  const awaitingMe = rows.filter(
    (r) => r.department === 'surgery' && !r.doctorDecision && String(r.doctorId) === String(doctorId),
  );
  const rest = rows.filter((r) => !awaitingMe.includes(r));

  return (
    <div className="space-y-3">
      {awaitingMe.map((r) => (
        <RequestCard key={r.id} request={r}>
          <SurgeryDecision request={r} onDone={load} />
        </RequestCard>
      ))}
      {rest.map((r) => (
        <RequestCard key={r.id} request={r} />
      ))}
    </div>
  );
}

export function HospitalDoctorPanel({ current }) {
  const [file, setFile] = useState({ loading: false, patient: null });
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!current) {
      setFile({ loading: false, patient: null });
      setSummary(null);
      return undefined;
    }
    let cancelled = false;
    setFile({ loading: true, patient: null });
    patientsApi
      .byBooking(current.id)
      .then((r) => !cancelled && setFile({ loading: false, patient: r.patient }))
      .catch(() => !cancelled && setFile({ loading: false, patient: null }));
    aiBatchApi
      .summaries({ bookingIds: String(current.id) })
      .then((r) => !cancelled && setSummary(Array.isArray(r) && r.length ? r[0] : null))
      .catch(() => !cancelled && setSummary(null));
    return () => {
      cancelled = true;
    };
  }, [current]);

  return (
    <div className="mt-8 space-y-6">
      {/* The numbered turns of the doctor's own clinics — "التالي" here moves
          the hall screen and every patient's phone (QA report §5.3). */}
      <section className="glass-panel rounded-2xl p-5">
        <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-white">
          <Icon name="list-ordered" size={18} className="text-cyan-300" /> أدوار عياداتي
        </h3>
        <LiveQueueBoard compact />
      </section>

      {current && (
        <section className="glass-panel rounded-2xl p-5">
          <h3 className="mb-4 flex items-center gap-2 text-base font-bold text-white">
            <Icon name="hospital" size={18} className="text-cyan-300" /> ملف المستشفى — {current.patientName}
          </h3>

          {summary && (
            <div className="mb-4 rounded-xl border border-purple-500/25 bg-purple-500/[0.07] p-3">
              <p className="mb-1 flex items-center gap-1.5 text-xs font-bold text-purple-200">
                <Icon name="sparkles" size={13} /> ملخص الذكاء الاصطناعي (جهّزته السكرتارية) · {formatDateTime(summary.createdAt)}
              </p>
              <p className="max-h-48 overflow-y-auto whitespace-pre-line text-sm text-slate-100">{summary.text}</p>
              <p className="mt-1 text-[11px] text-slate-500">ملخص مساعد وليس تشخيصاً.</p>
            </div>
          )}

          {file.loading ? (
            <Spinner label="جارٍ البحث عن ملف المريض" variant="inline" />
          ) : file.patient ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Tile3D icon="file-heart" label="الملف الطبي" hint="التشخيصات والنتائج" tone="cyan" enter onClick={() => setOpen('file')} />
              <Tile3D icon="clipboard-plus" label="طلب خدمة" hint="تحاليل، أشعة، أدوية، عمليات" tone="purple" enter delay={1} onClick={() => setOpen('request')} />
              <Tile3D icon="qr-code" label="رابط المريض" hint="ملفه على هاتفه" tone="emerald" enter delay={2} onClick={() => setOpen('portal')} />
              <Tile3D icon="notebook-pen" label="تشخيص وملاحظات" hint="يُضاف للملف" tone="amber" enter delay={3} onClick={() => setOpen('file')} />
            </div>
          ) : (
            <p className="rounded-xl bg-amber-500/10 p-3 text-sm text-amber-200">
              لم يُربط هذا الحجز بملف في المستشفى بعد — يربطه الاستقبال من شاشة حجوزات اليوم.
            </p>
          )}
          <p className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-500">
            <Icon name="shield-check" size={12} /> طلبات الخدمات تعتمدها إدارة التعاقدات مالياً — لا يعتمدها الطبيب.
          </p>
        </section>
      )}

      <section className="glass-panel rounded-2xl p-5">
        <h3 className="mb-4 flex flex-wrap items-center gap-2 text-base font-bold text-white">
          <Icon name="list-todo" size={18} className="text-purple-300" /> طلباتي والعمليات بانتظار موافقتي
          <Badge className="border-white/10 bg-white/5 text-slate-300">تُحدَّث تلقائياً</Badge>
        </h3>
        <MyRequests refreshKey={refreshKey} />
      </section>

      <Modal open={open === 'file'} onClose={() => setOpen(null)} title={file.patient?.fullName || 'الملف الطبي'} icon="file-heart" size="xl">
        {file.patient && <MedicalFileView patientId={file.patient.id} bookingId={current?.id} />}
      </Modal>
      <Modal open={open === 'request'} onClose={() => setOpen(null)} title="طلب خدمة للمريض" icon="clipboard-plus" size="xl">
        {file.patient ? (
          <RequestComposer
            patient={file.patient}
            bookingId={current?.id}
            onCreated={() => {
              setOpen(null);
              setRefreshKey((k) => k + 1);
            }}
          />
        ) : (
          <EmptyState icon="unlink" title="لا يوجد ملف مرتبط" />
        )}
      </Modal>
      <Modal open={open === 'portal'} onClose={() => setOpen(null)} title="رابط المريض" icon="qr-code" size="lg">
        {file.patient && <PortalLinkPanel patient={file.patient} />}
      </Modal>
    </div>
  );
}

export default HospitalDoctorPanel;
